import { Popover, styled } from '@mui/material'
import { useCallback, useMemo } from 'react'
import {
  SearchItemLabel,
  usePopup,
} from '../../../../../components/search-forms/search-item-labels'
import { intl } from '../../../../../../i18n'
import { SelectOption } from '../../../../../model/selectOption'
import { SprintGroupedCheckBoxList } from '../SprintGroupedCheckBoxPopper'
import { TeamSprintGroup } from '../sprintGroupTypes'

const PopoverContent = styled('div')({
  padding: '10px',
  minWidth: '445px',
  maxHeight: '300px',
  overflowY: 'auto',
})

type SprintGroupedConditionItemLabelProps = {
  value: string[] | undefined
  teamSprintGroups: TeamSprintGroup[]
  sprintOptions: SelectOption[]
  isLoading?: boolean
  onChange: (value: string[] | undefined) => void
  search: () => void
  onClose?: () => void
}

export const SprintGroupedConditionItemLabel = ({
  value,
  teamSprintGroups,
  sprintOptions,
  isLoading,
  onChange,
  search,
  onClose,
}: SprintGroupedConditionItemLabelProps) => {
  const { ref, open, openPopup, closePopup } = usePopup()

  const selectedOptions = useMemo(() => {
    return sprintOptions.filter(op => (value || []).includes(op.value))
  }, [value, sprintOptions])

  const valueNames = useMemo(() => {
    return selectedOptions.map(op => op.name)
  }, [selectedOptions])

  const onClear = useCallback(() => {
    onChange(undefined)
    onClose && onClose()
  }, [onChange, onClose])

  const onClosePopup = useCallback(() => {
    search()
    closePopup()
  }, [search, closePopup])

  const handleChange = useCallback(
    (newValue: string[] | undefined) => {
      onChange(newValue)
      if (!newValue || newValue.length === 0) {
        search()
        closePopup()
        if (onClose) onClose()
      }
    },
    [onChange, search, closePopup, onClose]
  )

  if (selectedOptions.length === 0) return <></>

  const header = intl.formatMessage({
    id: 'wbsItemSearchFilterPanel.sprint',
  })

  return (
    <>
      <SearchItemLabel
        ref={ref}
        active={open}
        onClick={openPopup}
        onClear={onClear}
        isLoading={isLoading}
      >
        {header}: {valueNames.join(',')}
      </SearchItemLabel>
      <Popover
        open={open && !isLoading}
        anchorEl={ref.current || null}
        onClose={onClosePopup}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <PopoverContent>
          <SprintGroupedCheckBoxList
            teamSprintGroups={teamSprintGroups}
            value={value}
            onChange={handleChange}
          />
        </PopoverContent>
      </Popover>
    </>
  )
}
